import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
} from '@angular/core';
import {DatatableService} from '@common/datatable/datatable.service';
import {CurrentUser} from '@common/auth/current-user';
import {Toast} from '@common/core/ui/toast.service';
import {ActivatedRoute, Router} from '@angular/router';
import {BackendErrorResponse} from '@common/core/types/backend-error-response';
import {HttpErrors} from '@common/core/http/errors/http-errors.enum';
import {Observable, Subscription} from 'rxjs';
import {WorkspacesService} from '@common/workspaces/workspaces.service';
import {LINK_GROUP_INDEX_FILTERS} from '../../link-group/link-group-index/link-group-index-filters';
import {LinkUsageService} from '../../../shared/link/link-usage.service';
import {ActiveAppSectionService} from '../../active-app-section.service';
import {Biolink} from '../biolink';
import {Modal} from '@common/core/ui/dialogs/modal.service';
import {
    CrupdateLinkGroupModalComponent,
    CrupdateLinkGroupModalData,
} from '../../link-group/crupdate-link-group-modal/crupdate-link-group-modal.component';
import {BiolinksService} from '../biolinks.service';

@Component({
    selector: 'biolink-index',
    templateUrl: './biolink-index.component.html',
    styleUrls: ['./biolink-index.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [DatatableService],
})
export class BiolinkIndexComponent implements OnInit, OnDestroy {
    biolinks$ = this.datatable.data$ as Observable<Biolink[]>;
    filters = LINK_GROUP_INDEX_FILTERS;
    private workspaceSub: Subscription;

    constructor(
        public datatable: DatatableService<Biolink>,
        public currentUser: CurrentUser,
        private biolinks: BiolinksService,
        private toast: Toast,
        private route: ActivatedRoute,
        private linkUsage: LinkUsageService,
        private activeSection: ActiveAppSectionService,
        private workspaces: WorkspacesService,
        private modal: Modal,
        private router: Router
    ) {}

    ngOnInit() {
        this.workspaceSub = this.workspaces.activeId$.subscribe(() => {
            this.setFilters();
        });
        this.datatable.init({
            uri: BiolinksService.BASE_URI,
            staticParams: {
                with: ['user'],
                withCount: ['links'],
                userId: !this.showUserColumn()
                    ? this.currentUser.get('id')
                    : null,
            },
        });
    }

    ngOnDestroy() {
        this.workspaceSub.unsubscribe();
    }

    maybeDeleteSelectedBiolinks(biolinkIds?: number[]) {
        this.datatable.confirmResourceDeletion('biolinks').subscribe(() => {
            this.biolinks
                .delete(biolinkIds || this.datatable.selectedRows$.value)
                .subscribe(
                    () => {
                        this.datatable.reset();
                        this.toast.open('Biolink deleted');
                    },
                    (errResponse: BackendErrorResponse) => {
                        this.toast.open(
                            errResponse.message || HttpErrors.Default
                        );
                    }
                );
        });
    }

    showUserColumn(): boolean {
        // show in admin and if active workspace is not personal
        return this.activeSection.admin || !!this.workspaces.activeId$.value;
    }

    private setFilters() {
        this.filters = LINK_GROUP_INDEX_FILTERS.filter(
            f => f.key !== 'user_id' || this.showUserColumn()
        );
    }

    openCreateBiolinkModal() {
        this.modal
            .open(CrupdateLinkGroupModalComponent, {
                type: 'biolink',
            } as CrupdateLinkGroupModalData)
            .afterClosed()
            .subscribe(biolink => {
                if (biolink) {
                    const prefix = this.activeSection.admin
                        ? 'admin'
                        : 'dashboard';
                    this.router.navigate([
                        '/',
                        prefix,
                        'biolinks',
                        biolink.id,
                        'edit',
                    ]);
                }
            });
    }
}
